import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { leaveApi, leaveLimitsApi, toilApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { toast } from 'sonner';
import { format } from 'date-fns';
import DateRangeField from './DateRangeField';
import { LEAVE_REASON_HINT } from '../lib/leaveReasonHint';
import { bookedMessage, outcomeLine, exceptionLine, submitLabel } from '../lib/bookingOutcome';
import { clockTime } from '../lib/bookingTime';

const RequestLeaveDialog = ({ open, onOpenChange, onSuccess, initialDate, initialLeaveTypeId, bookForUserId, bookForUserName, allowPastDates = false }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [breaches, setBreaches] = useState(null);
  const [calc, setCalc] = useState(null);   // schedule-aware day-count preview
  // What happens when they press the button (H9): approved straight away, or who it goes
  // to. From the SAME rules check the booking route runs, so it can't promise otherwise.
  const [outcome, setOutcome] = useState(null);
  // Bookable granularity: the company setting + the "part of the day" state.
  const [granularity, setGranularity] = useState('half_day');
  const [timed, setTimed] = useState(false);
  const [startMinute, setStartMinute] = useState(null);
  const [endMinute, setEndMinute] = useState(null);
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: null,
    end_date: null,
    start_type: 'morning',
    end_type: 'end_of_day',
    reason: ''
  });

  const TIMED_STEPS = { hour: 60, '30m': 30, '15m': 15 };
  // Time in lieu (DESIGN-toil.md K): ALWAYS bookable in hours, whatever the company books
  // holiday in — quarter hours unless the company already books finer. The server says the same.
  const selectedType = leaveTypes.find((t) => t.id === formData.leave_type_id);
  const isToil = !!(selectedType && selectedType.is_toil);
  const timedStep = TIMED_STEPS[granularity] || (isToil ? 15 : null);
  // The TOIL balance check (H) — the SAME check the booking route makes (POST /toil/check).
  const [toilCheck, setToilCheck] = useState(null);
  const singleDay = !!(formData.start_date && formData.end_date &&
    format(formData.start_date, 'yyyy-MM-dd') === format(formData.end_date, 'yyyy-MM-dd'));
  const timedActive = !!timedStep && singleDay && timed;

  const hhmm = clockTime;  // the shared 24-hour formatter (lib/bookingTime, C16)
  const timeOptions = [];
  if (timedStep) {
    for (let m = 6 * 60; m <= 22 * 60; m += timedStep) timeOptions.push(m);
  }

  useEffect(() => {
    if (open) {
      fetchLeaveTypes();
      setBreaches(null);
      // Which granularity has the company chosen? Fails closed to half days.
      leaveApi.getBookingGranularity()
        .then(r => setGranularity(r.data?.booking_granularity || 'half_day'))
        .catch(() => setGranularity('half_day'));
      setTimed(false);
      setStartMinute(null);
      setEndMinute(null);
      // Set initial values if provided, otherwise reset
      setFormData({
        leave_type_id: initialLeaveTypeId || '',
        start_date: initialDate || null,
        end_date: initialDate || null,
        start_type: 'morning',
        end_type: 'end_of_day',
        reason: ''
      });
    }
  // Runs once on mount. `fetchLeaveTypes` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDate, initialLeaveTypeId]);

  // Live, schedule-aware preview of how many days this booking will deduct.
  useEffect(() => {
    if (!open || !formData.start_date || !formData.end_date) { setCalc(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await leaveApi.calculateDays({
          start_date: format(formData.start_date, 'yyyy-MM-dd'),
          end_date: format(formData.end_date, 'yyyy-MM-dd'),
          start_type: formData.start_type,
          end_type: formData.end_type,
          user_id: bookForUserId || undefined,
          ...(timedActive && startMinute !== null && endMinute !== null && endMinute > startMinute
            ? { start_minute: startMinute, end_minute: endMinute }
            : {}),
        });
        if (!cancelled) setCalc(res.data);
      } catch (e) {
        if (!cancelled) setCalc(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, formData.start_date, formData.end_date, formData.start_type, formData.end_type, bookForUserId,
      timedActive, startMinute, endMinute]);

  // Live: will this be approved straight away? (H9). A failed check shows nothing — the
  // route decides on submit regardless, and the toast afterwards says what happened.
  const outcomeArgs = () => ({
    user_id: bookForUserId || undefined,
    leave_type_id: formData.leave_type_id,
    start_date: format(formData.start_date, 'yyyy-MM-dd'),
    end_date: format(formData.end_date, 'yyyy-MM-dd'),
    start_type: formData.start_type,
    end_type: formData.end_type,
    ...(timedActive && startMinute !== null && endMinute !== null && endMinute > startMinute
      ? { start_minute: startMinute, end_minute: endMinute }
      : {}),
  });
  useEffect(() => {
    if (!open || !formData.leave_type_id || !formData.start_date || !formData.end_date) {
      setOutcome(null); return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await leaveLimitsApi.check(outcomeArgs());
        if (!cancelled) setOutcome(res.data?.outcome || null);
      } catch (e) {
        if (!cancelled) setOutcome(null);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  // outcomeArgs reads exactly the values listed here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.leave_type_id, formData.start_date, formData.end_date, formData.start_type,
      formData.end_type, bookForUserId, timedActive, startMinute, endMinute]);

  // Live: does this fit in their time in lieu, and if not, why? (DESIGN-toil.md screen 6)
  useEffect(() => {
    if (!open || !isToil || !formData.start_date || !formData.end_date) { setToilCheck(null); return; }
    if (timedActive && !(startMinute !== null && endMinute !== null && endMinute > startMinute)) {
      setToilCheck(null); return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await toilApi.check(outcomeArgs());
        if (!cancelled) setToilCheck(res.data);
      } catch (e) {
        if (!cancelled) setToilCheck(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  // outcomeArgs reads exactly the values listed here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isToil, formData.start_date, formData.end_date, formData.start_type, formData.end_type,
      bookForUserId, timedActive, startMinute, endMinute]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveApi.getTypes();
      setLeaveTypes(response.data);
      // Only set default leave type if no initial one was provided
      if (response.data.length > 0 && !initialLeaveTypeId) {
        setFormData(prev => ({ ...prev, leave_type_id: response.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  };

  const doCreate = async () => {
    setLoading(true);
    try {
      const requestData = {
        ...formData,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd'),
        ...(timedActive ? { start_minute: startMinute, end_minute: endMinute } : {}),
      };
      if (bookForUserId) {
        requestData.user_id = bookForUserId;
      }
      const _res = await leaveApi.createRequest(requestData);
      // ⚠️ The booking was TRIMMED because they were already off for part of the
      // range they asked for (services/leave_overlap.py). Say so loudly and
      // leave it up for longer — an app that quietly books different dates from
      // the ones somebody chose is how "it lost my leave request" starts.
      const _overlapNote = _res?.data?.overlap_note;
      if (_overlapNote) {
        // ⚠️ STAYS UNTIL DISMISSED (Rob, 01/09/2026). We booked different dates
        // from the ones they picked; a message that vanishes after ten seconds
        // is one they can miss entirely, and then the app has silently changed
        // their leave as far as they're concerned.
        toast.warning(_overlapNote, { duration: Infinity, closeButton: true });
      } else if (_res?.data?.auto_approved) {
        // H9: it approved itself — say so, with the dates, rather than "submitted".
        toast.success(bookedMessage(_res.data, bookForUserName), {
          description: "It was within the rules, so it didn't need to wait for anyone.",
        });
      } else {
        toast.success(bookForUserName ? `Leave request submitted for ${bookForUserName}!` : 'Leave request submitted!');
      }
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (timedActive) {
      if (startMinute === null || endMinute === null) {
        toast.error('Pick the start and end times');
        return;
      }
      if (endMinute <= startMinute) {
        toast.error('The end time must be after the start time');
        return;
      }
    }
    // Soft concurrent-off limit check — warn, don't block
    try {
      const res = await leaveLimitsApi.check(outcomeArgs());
      if (res.data?.outcome) setOutcome(res.data.outcome);
      if (res.data && res.data.breaches && res.data.breaches.length) {
        setBreaches(res.data.breaches);
        return;
      }
    } catch (e) { /* if the check fails, don't block booking */ }
    await doCreate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {bookForUserName ? `Request leave for ${bookForUserName}` : 'Request leave'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Leave Type */}
          <div className="space-y-2">
            <Label>Leave type</Label>
            <Select 
              value={formData.leave_type_id} 
              onValueChange={(value) => setFormData({ ...formData, leave_type_id: value })}
            >
              <SelectTrigger className="input-hive" data-testid="dialog-leave-type-select">
                <SelectValue placeholder="Select leave type" />
              </SelectTrigger>
              <SelectContent>
                {leaveTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color }} />
                      {type.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range — ONE pop-up for both days (DateRangeField; Rob 1A 2B 3A 4B, 02/11/2026) */}
          <DateRangeField
            start={formData.start_date}
            end={formData.end_date}
            onChange={({ start, end }) => setFormData({ ...formData, start_date: start, end_date: end })}
            disabled={(date) => allowPastDates ? false : date < new Date(new Date().setHours(0, 0, 0, 0))}
            startLabel="Start date"
            endLabel="End date"
            startTestId="dialog-start-date-picker"
            endTestId="dialog-end-date-picker"
            endDisabled={!formData.start_date}
          />

          {/* Part of the day (bookable granularity) — offered only when the
              company allows timed bookings AND it's a single-day request. */}
          {timedStep && singleDay && (
            <label className="flex items-center gap-3 cursor-pointer" data-testid="dialog-timed-toggle">
              <input
                type="checkbox"
                checked={timed}
                onChange={(e) => setTimed(e.target.checked)}
                className="w-4 h-4 rounded border-input accent-primary"
              />
              <span className="text-sm">
                Book part of the day
                <span className="text-muted-foreground"> — times in {timedStep === 60 ? '1-hour' : `${timedStep}-minute`} steps</span>
              </span>
            </label>
          )}

          {/* Timed window OR the classic half-day dropdowns (hidden entirely
              when company policy is full days only — the server enforces too). */}
          {timedActive ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From</Label>
                <select
                  value={startMinute === null ? '' : startMinute}
                  onChange={(e) => setStartMinute(e.target.value === '' ? null : parseInt(e.target.value))}
                  className="input-hive w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="dialog-timed-from"
                >
                  <option value="">Pick a time</option>
                  {timeOptions.slice(0, -1).map((m) => (
                    <option key={m} value={m}>{hhmm(m)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Until</Label>
                <select
                  value={endMinute === null ? '' : endMinute}
                  onChange={(e) => setEndMinute(e.target.value === '' ? null : parseInt(e.target.value))}
                  className="input-hive w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="dialog-timed-until"
                >
                  <option value="">Pick a time</option>
                  {timeOptions.filter((m) => startMinute === null || m > startMinute).map((m) => (
                    <option key={m} value={m}>{hhmm(m)}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : granularity !== 'full_day' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start</Label>
                <Select
                  value={formData.start_type}
                  onValueChange={(value) => setFormData({ ...formData, start_type: value })}
                >
                  <SelectTrigger className="input-hive">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning</SelectItem>
                    <SelectItem value="afternoon">Afternoon</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>End</Label>
                <Select
                  value={formData.end_type}
                  onValueChange={(value) => setFormData({ ...formData, end_type: value })}
                >
                  <SelectTrigger className="input-hive">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lunchtime">Lunchtime</SelectItem>
                    <SelectItem value="end_of_day">End of day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {/* Reason */}
          <div className="space-y-2">
            <Label>Reason (optional)</Label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Add a note for your manager..."
              className="input-hive resize-none"
              rows={3}
              data-testid="dialog-leave-reason"
            />
            <p className="text-xs text-muted-foreground" data-testid="dialog-leave-reason-hint">{LEAVE_REASON_HINT}</p>
          </div>

          {/* Concurrent-off limit warning */}
          {breaches && breaches.length > 0 && (
            <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-bold text-red-600">This request breaks a company rule</p>
                  <ul className="mt-1 space-y-0.5 text-red-600 font-medium">
                    {breaches.map((b, i) => {
                      if (b.message) return <li key={i}>{b.message}</li>;
                      const who = (b.who && b.who.length) ? b.who.join(', ') : 'someone';
                      const are = (b.who && b.who.length > 1) ? 'are' : 'is';
                      return (
                        <li key={i}>{who} {are} already off{b.peak_date ? ` on ${b.peak_date}` : ''} — {b.policy_text || 'this exceeds a leave limit'}.</li>
                      );
                    })}
                  </ul>
                  <p className="mt-1 text-muted-foreground">{exceptionLine(outcome)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Time in lieu: where it comes from, or why it doesn't fit (DESIGN-toil.md H, F). */}
          {isToil && toilCheck && (
            toilCheck.ok ? (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300" data-testid="dialog-toil-ok">
                <p className="font-semibold">{toilCheck.cost} from {bookForUserName ? 'their' : 'your'} time in lieu.</p>
                {(toilCheck.uses || []).some((u) => u.use_by) && (
                  <p className="text-xs mt-0.5">
                    The time that must be used soonest goes first:{' '}
                    {toilCheck.uses.map((u) => (u.use_by
                      ? `${u.amount} due by ${formatDateGB(u.use_by)}`
                      : `${u.amount} that doesn't expire`)).join(', then ')}.
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-600" data-testid="dialog-toil-short">
                {toilCheck.message}
              </div>
            )
          )}

          {/* Schedule-aware deduction preview */}
          {calc && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm" data-testid="dialog-days-preview">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{isToil ? 'From time in lieu' : 'Deducted from allowance'}</span>
                <span className="font-semibold">
                  {(() => {
                    const mins = calc.total_minutes || 0;
                    const hm = `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`;
                    if (calc.timed) return hm;
                    // ⚠️ SOMEBODY COUNTED IN HOURS SEES HOURS — only (Rob, 22/10/2026):
                    // "0.75 days (3h)" for a 3-hour Wednesday read as a fraction of a day
                    // they never think in. Their balance, and the check, are in hours.
                    if (calc.unit === 'hours') return hm;
                    // Days + the hours translation for EVERYONE (Rob, 16 Jul):
                    // the hours come from the minutes engine — the exact cost
                    // that will be stamped — so uneven days read honestly
                    // ("1 day (3h)" for a mornings-only Tuesday).
                    const days = `${calc.total_days} ${calc.total_days === 1 ? 'day' : 'days'}`;
                    return mins > 0 ? `${days} (${hm})` : days;
                  })()}
                </span>
              </div>
              {(() => {
                const line = outcomeLine(outcome);
                // Once the red box is up it already says who it goes to and why.
                if (!line || (line.tone !== 'auto' && breaches && breaches.length)) return null;
                return line.tone === 'auto' ? (
                  <p className="mt-1.5 text-xs text-emerald-600 flex items-center gap-1" data-testid="dialog-auto-approved-note">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />{line.text}
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-muted-foreground" data-testid="dialog-goes-to-note">{line.text}</p>
                );
              })()}
              {calc.timed && (calc.total_minutes || 0) === 0 && (
                <p className="mt-1 text-xs text-amber-500">
                  That time is outside their working hours — nothing would be deducted.
                </p>
              )}
              {(calc.skipped_bank_holidays > 0 || calc.non_working_days > 0) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {[
                    calc.skipped_bank_holidays > 0 ? `skips ${calc.skipped_bank_holidays} bank holiday${calc.skipped_bank_holidays > 1 ? 's' : ''}` : null,
                    calc.non_working_days > 0 ? `${calc.non_working_days} non-working day${calc.non_working_days > 1 ? 's' : ''} not counted` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {breaches && breaches.length > 0 ? (
              <Button
                type="button"
                className="btn-primary bg-red-600 hover:bg-red-700"
                disabled={loading}
                onClick={() => { setBreaches(null); doCreate(); }}
                data-testid="dialog-ask-anyway"
              >
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>) : 'Ask anyway'}
              </Button>
            ) : (
              <Button 
                type="submit" 
                className="btn-primary"
                disabled={loading || (isToil && toilCheck && !toilCheck.ok)}
                data-testid="dialog-submit-leave"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  submitLabel(outcome)
                )}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RequestLeaveDialog;
